import type { BuilderFailure, BuilderRequestError } from "../api/errors";

/**
 * What the workspace may show.
 * - `revoked`: the server says the session ended or the site is not this owner's. Hide private content,
 *   even if a cache still holds an earlier answer.
 * - `disconnected`: the last read failed transiently; the earlier answer may be shown, marked as possibly out of date.
 */
export type WorkspaceAccess =
  | { kind: "loading" }
  | { kind: "ok" }
  | { kind: "disconnected"; failure: BuilderFailure }
  | { kind: "revoked"; failure: "signed-out" | "not-found" }
  | { kind: "unavailable"; failure: BuilderFailure };

export function workspaceAccess(options: {
  hasData: boolean;
  isLoading: boolean;
  error: BuilderRequestError | null;
  /** A failure seen by any other request, e.g. the publication flow. */
  otherFailure?: BuilderFailure | null;
}): WorkspaceAccess {
  const failures = [options.error?.failure, options.otherFailure].filter((f): f is BuilderFailure => Boolean(f));
  const revoked = failures.find((f): f is "signed-out" | "not-found" => f === "signed-out" || f === "not-found");
  if (revoked) return { kind: "revoked", failure: revoked };
  if (options.error) {
    return options.hasData
      ? { kind: "disconnected", failure: options.error.failure }
      : { kind: "unavailable", failure: options.error.failure };
  }
  if (!options.hasData) return options.isLoading ? { kind: "loading" } : { kind: "unavailable", failure: "unavailable" };
  return { kind: "ok" };
}
